import { Link } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import MobileMenu from '../../components/feature/MobileMenu';
import { useSEO } from '../../hooks/useSEO';
import JsonLd from '../../components/feature/JsonLd';

export default function WorkPage() {
  useSEO({
    title: 'Our Work | Case Studies & Projects | Healthcare, Government & Social Impact',
    description: 'Explore our portfolio of human-centred design projects across healthcare, government, and social sectors. Real-world case studies showcasing innovation and impact with leading organizations.',
    keywords: 'case studies, design portfolio, healthcare projects, government innovation, social impact design, client work, design thinking examples',
    canonical: '/work',
    ogType: 'website',
    ogImage: 'https://public.readdy.ai/ai/img_res/0491b2d3a4c9fbff02c0e274c60ae3fe.jpg'
  });

  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState('Selected Work');
  const [showBackToTop, setShowBackToTop] = useState(false);
  const clientListRef = useRef<HTMLDivElement>(null);
  const caseStudiesSectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      setShowBackToTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCategoryChange = (category: string) => {
    setActiveCategory(category);
    setTimeout(() => {
      if (caseStudiesSectionRef.current) {
        const offset = caseStudiesSectionRef.current.getBoundingClientRect().top + window.scrollY - 120;
        window.scrollTo({ top: offset, behavior: 'smooth' });
      }
    }, 50);
  };

  const categories = ['Selected Work', 'Healthcare', 'Government & Not for Profit', 'Various Sectors', 'Clients'];

  const caseStudies = [
    {
      id: 1,
      title: 'Designing patient first clinical trials through a patient app',
      client: 'Global pharmaceutical company',
      category: 'Healthcare',
      featured: true,
      image: 'https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/ee9b8a579b0f0dfe46dd47075426190b.jpeg',
      link: '/work/chiesi-patient-first-clinical-trials'
    },
    {
      id: 7,
      title: 'Revitalising mental health in Hong Kong through AR & AI',
      client: 'Google',
      category: 'Healthcare',
      featured: true,
      image: 'https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/983b2cf2226e0ba05a8262de5bbfcb7c.jpeg',
      link: '/work/google-ar-mental-health'
    },
    {
      id: 2,
      title: 'Using patient archetypes to shape clinical development',
      client: 'Global pharmaceutical company',
      category: 'Healthcare',
      featured: true,
      image: 'https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/6c69339dc5a77859d2a0cfae4ed16ef8.jpeg',
      link: '/work/chiesi-patient-archetypes'
    },
    {
      id: 18,
      title: 'Improving the experience of women fleeing domestic violence',
      client: 'The Good Samaritan Inn & Victorian Government',
      category: 'Government & Not for Profit',
      featured: true,
      image: 'https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/a8131d8083980545235293ed39dce142.jpeg',
      link: '/work/good-samaritan-inn-domestic-violence'
    },
    {
      id: 5,
      title: 'Reimagining how crucial vaccines are brought to market',
      client: 'Top 10 Pharmaceutical Company',
      category: 'Healthcare',
      featured: true,
      image: 'https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/59ca997d5d37a61c379ae60ca4f40e99.jpeg',
      link: '/work/astrazeneca-vaccines'
    },
    {
      id: 14,
      title: 'Changing the way tourists visit the world\'s largest coral reef to keep it thriving',
      client: 'Tourism Tropical North Queensland',
      category: 'Government & Not for Profit',
      featured: true,
      image: 'https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/63681d5d3cbfa151a1002be71469addf.jpeg',
      link: '/work/tourism-tropical-north-queensland'
    },
    {
      id: 6,
      title: 'Ending the world\'s oldest and deadliest disease',
      client: 'Malaria No More',
      category: 'Healthcare',
      featured: false,
      image: 'https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/a06f54a857c41d4cbe8f3fb37f4f8f71.jpeg',
      link: '/work/malaria-no-more'
    },
    {
      id: 10,
      title: 'Transforming the patient care experience across every phase',
      client: 'ConvaTec',
      category: 'Healthcare',
      featured: false,
      image: 'https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/7267f85f5ccc07f058256cd1cf369acd.jpeg',
      link: '/work/convatec-patient-care'
    },
    {
      id: 8,
      title: 'Getting life saving plasma to those who need it most',
      client: 'Australian Red Cross Blood Service',
      category: 'Healthcare',
      featured: false,
      image: 'https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/6ebaadf85ce4b26263f432cd39d36f8d.jpeg',
      link: '/work/australian-red-cross-plasma'
    },
    {
      id: 13,
      title: 'Supporting families navigate cancer treatment and the journey home',
      client: 'Camp Quality',
      category: 'Healthcare',
      featured: false,
      image: 'https://public.readdy.ai/ai/img_res/edited_08dcfee1391436c0d865020a4f9fe88b_585d55ce.jpg',
      link: '/work/camp-quality-new-normal-navigator'
    },
    {
      id: 3,
      title: 'Creating a patient experience framework to set the foundations for change',
      client: 'Global pharmaceutical company',
      category: 'Healthcare',
      featured: false,
      image: 'https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/e7148a0e3de63bc88e3e7e813263df57.jpeg',
      link: '/work/chiesi-patient-experience-framework'
    },
    {
      id: 4,
      title: 'Transforming how patient insights are gathered, stored and used in a complex environment',
      client: 'Global pharmaceutical company',
      category: 'Healthcare',
      featured: false,
      image: 'https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/403472c7ae6592343ba0e169a5753aee.jpeg',
      link: '/work/gsk-viiv-patient-insights'
    },
    {
      id: 11,
      title: 'Helping chronically ill teenagers transition to better health care',
      client: 'Monash Hospital',
      category: 'Healthcare',
      featured: false,
      image: 'https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/3225b8391bf9aaae4aff6a1a542fc2cf.jpeg',
      link: '/work/monash-hospital-teenagers'
    },
    {
      id: 9,
      title: 'Improving liver cancer treatment for patients & doctors',
      client: 'Top 10 Pharmaceutical Company',
      category: 'Healthcare',
      featured: false,
      image: 'https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/d654018d15e7499296428d0262ccfb1e.jpeg',
      link: '/work/astrazeneca-liver-cancer'
    },
    {
      id: 12,
      title: 'Changing the way communities access health care and visit their doctor',
      client: 'East Brunswick Medical Centre',
      category: 'Healthcare',
      featured: false,
      image: 'https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/65e4b3a231f765d88a983851ba9becb4.jpeg',
      link: '/work/east-brunswick-medical-centre'
    },
    {
      id: 21,
      title: 'Transforming child care services for all Australians',
      client: 'Australian Government — Digital Transformation Agency',
      category: 'Government & Not for Profit',
      featured: false,
      image: 'https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/539c84e5a4ad46a9781e879fecfb040d.jpeg',
      link: '/work/australian-government-child-care'
    },
    {
      id: 15,
      title: 'Creating an innovation toolkit for the UN\'s 2030 Sustainability Agenda',
      client: 'United Nations',
      category: 'Government & Not for Profit',
      featured: false,
      image: 'https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/7a741e0401ad7f2e45c8e5ee74bd16e4.jpeg',
      link: '/work/united-nations-innovation-toolkit'
    },
    {
      id: 16,
      title: 'Creating a lighter environmental footprint with Australia\'s biggest airline',
      client: 'QANTAS Airlines',
      category: 'Government & Not for Profit',
      featured: false,
      image: 'https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/4ad0a90dbe5e416105555825b8b1142f.jpeg',
      link: '/work/qantas-sustainability'
    },
    {
      id: 17,
      title: 'Tackling disadvantage in schools to support vulnerable kids',
      client: 'Adobe, Swinburne University & Victorian Government',
      category: 'Government & Not for Profit',
      featured: false,
      image: 'https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/53f7ea1a2396188884d51729562c5e8f.jpeg',
      link: '/work/adobe-swinburne-vulnerable-kids'
    },
    {
      id: 19,
      title: 'Reimagining funding strategies for communities in need',
      client: 'World Vision',
      category: 'Government & Not for Profit',
      featured: false,
      image: 'https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/034e87d049dfecb8cebff17f0d1e9b5f.jpeg',
      link: '/work/world-vision-funding-strategies'
    },
    {
      id: 20,
      title: 'Helping grassroots organisations support refugees',
      client: 'For Refugees',
      category: 'Government & Not for Profit',
      featured: false,
      image: 'https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/09217488e3035e03e862d2bfec2f6873.jpeg',
      link: '/work/for-refugees'
    },
    {
      id: 24,
      title: 'Improving customer journeys during flight delays',
      client: 'Jetstar Airlines',
      category: 'All other sectors',
      featured: true,
      image: 'https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/9e02e6ffe00a1d7c89715bb190f7985c.png',
      link: '/work/jetstar-flight-delays'
    },
    {
      id: 23,
      title: 'Building customer experience capability at scale',
      client: 'ANZ Bank',
      category: 'All other sectors',
      featured: false,
      image: 'https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/c1355d6782632938a4e39b50fda48bd4.png',
      link: '/work/anz-bank-customer-experience'
    },
    {
      id: 25,
      title: 'Reframing digital growth around priority customer segments',
      client: 'Merlin Entertainments',
      category: 'All other sectors',
      featured: true,
      image: 'https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/53703d9b542b2cc694ffe0ba633452a9.jpeg',
      link: '/work/merlin-entertainments-digital-growth'
    },
    {
      id: 22,
      title: 'Designing a creative learning program for budding entrepreneurs',
      client: 'RMIT University — Activator Programme',
      category: 'All other sectors',
      featured: false,
      image: 'https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/d76f4c5f0a9e9e5255110e1cf8965722.jpeg',
      link: '/work/rmit-activator'
    },
    {
      id: 27,
      title: 'Transforming the painters ecosystem across digital & retail',
      client: 'Dulux',
      category: 'All other sectors',
      featured: false,
      image: 'https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/806b5990f6b8f076533cd25370c837a9.jpeg',
      link: '/work/dulux-painters-ecosystem'
    },
    {
      id: 31,
      title: 'An innovation lab to change how people work, learn, and collaborate',
      client: 'Australia Post',
      category: 'All other sectors',
      featured: false,
      image: 'https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/d2f9a300d0cb70229fc5583fc06613aa.jpeg',
      link: '/work/australia-post-innovation-lab'
    },
    {
      id: 30,
      title: 'Making it easier to send something overseas',
      client: 'Australia Post',
      category: 'All other sectors',
      featured: false,
      image: 'https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/2440086bf223f5f29bf2e8753a78aaad.jpeg',
      link: '/work/australia-post-international-posting'
    },
    {
      id: 26,
      title: 'Shifting beauty from transactions to empowerment through digital',
      client: 'Shiseido',
      category: 'All other sectors',
      featured: false,
      image: 'https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/28ae7bf51bb22294b27f1d1328abb261.jpeg',
      link: '/work/shiseido-beauty-empowerment'
    },
    {
      id: 28,
      title: 'Transforming how Australia Post empowers retail teams',
      client: 'Australia Post',
      category: 'All other sectors',
      featured: false,
      image: 'https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/f51239612ad3261deef39b206f89a51a.jpeg',
      link: '/work/australia-post-retail-teams'
    },
    {
      id: 29,
      title: 'Changing history — how mail is delivered across Australia',
      client: 'Australia Post',
      category: 'All other sectors',
      featured: false,
      image: 'https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/c5c40e9e56cca8dda124b128f6d36169.jpeg',
      link: '/work/australia-post-mail-delivery'
    }
  ];

  const clientList = [
    {
      sector: 'Life Sciences',
      clients: ['Pfizer', 'BioNTech', 'AstraZeneca', 'MSD', 'Bayer', 'Novo Nordisk', 'Chiesi', 'GSK', 'ViiV', 'Roche']
    },
    {
      sector: 'Health',
      clients: ['ConvaTec', 'World Health Organisation', 'Australian Blood Bank', 'Monash Hospital', 'East Brunswick Medical Centre', 'La Trobe Community Health', 'NHS (United Kingdom)']
    },
    {
      sector: 'Government',
      clients: ['Digital Transformation Agency', 'Department of Education', 'Department of Human Services', 'Department of Social Services', 'Australian Taxation Office', 'Queensland Government', 'Victoria Police']
    },
    {
      sector: 'Education',
      clients: ['RMIT University', 'Swinburne University', 'Australian Catholic University']
    },
    {
      sector: "Charities & Not For Profits",
      clients: ['The United Nations', 'Malaria No More', 'GAVI', 'World Vision', 'The Smith Family', 'Save The Children', 'Camp Quality', 'Smiling Mind', 'Indigenous Excellence Council', 'Good Samaritan Inn', 'For Refugees', "People's Post Code Lottery", 'Børns Vilkår (children\'s rights)']
    },
    {
      sector: "Start-ups",
      clients: ['Purposed (corporate CSR)', 'Unlocked (phone ad payment)', 'Spectrum (LGBTQIA+ safe space finder)', "Caremonger ('care' delivery service)", 'LVLY (flower delivery service)']
    },
    {
      sector: 'Technology',
      clients: ['Google', 'Salesforce', 'Adobe', 'Microsoft']
    },
    {
      sector: 'Beauty & Fashion',
      clients: ['Shiseido', 'Garnier', 'Loreal', 'Forever New', 'Bonds']
    },
    {
      sector: 'Travel & Lifestyle',
      clients: ['Merlin Entertainment', 'Jetstar Airlines', 'QANTAS Airlines', 'Tourism Tropical Queensland', 'Tennis Australia', 'Horse Racing Victoria', 'Best Bets', 'Dulux', 'Phillips', 'TV2 Denmark', 'AGL']
    },
    {
      sector: 'Logistics & Manufacturing',
      clients: ['Australia Post', 'Star Track', 'Holden', 'Nestle']
    },
    {
      sector: 'Finance & Insurance',
      clients: ['ANZ Bank', 'HSBC', 'Bank of Colombia', 'Royal Automobile Club of Victoria']
    }
  ];

  const filteredCaseStudies = activeCategory === 'Selected Work'
    ? caseStudies
    : activeCategory === 'Various Sectors'
    ? caseStudies.filter(study => study.category === 'All other sectors')
    : caseStudies.filter(study => study.category === activeCategory);

  return (
    <div className="min-h-screen bg-white">
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": "Our Work",
        "url": "https://example.com/work",
        "description": "Portfolio of human-centred design projects across healthcare, government, and social sectors",
        "about": {
          "@type": "CreativeWork",
          "name": "Design For Case Studies",
          "description": "Real-world case studies showcasing innovation and impact with leading organizations"
        }
      }} />
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-sm' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-24">
            <Link to="/" className="flex items-center">
              <img 
                src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/511aeba7affe24966a38635cdd087c13.png"
                alt="Design For" 
                className="h-16"
              />
            </Link>
            <div className="hidden md:flex items-center space-x-10">
              <Link to="/" className="text-black hover:text-primary transition-colors duration-200 text-sm font-medium uppercase tracking-wider">Home</Link>
              <Link to="/about" className="text-black hover:text-primary transition-colors duration-200 text-sm font-medium uppercase tracking-wider">About</Link>
              <Link to="/services" className="text-black hover:text-primary transition-colors duration-200 text-sm font-medium uppercase tracking-wider">Services</Link>
              <Link to="/work" className="text-primary transition-colors duration-200 text-sm font-medium uppercase tracking-wider">Work</Link>
              <Link to="/resources" className="text-black hover:text-[#ff4858] transition-colors duration-200 text-sm font-medium uppercase tracking-wider">Insights</Link>
              <Link to="/contact" className="bg-primary text-white px-6 py-3 rounded-full hover:bg-primary/90 transition-all duration-200 text-sm font-bold uppercase tracking-wider whitespace-nowrap">
                Contact
              </Link>
            </div>
            <button 
              onClick={() => setMobileMenuOpen(true)}
              className="md:hidden text-black"
            >
              <i className="ri-menu-line text-3xl"></i>
            </button>
          </div>
        </div>
      </nav>

      <MobileMenu isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />

      {/* Hero Section */}
      <section className="relative pt-48 pb-20 bg-white overflow-hidden min-h-[480px] flex items-end">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/a06f54a857c41d4cbe8f3fb37f4f8f71.jpeg"
            alt="Work hero background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full">
          <h1 className="text-7xl lg:text-8xl font-black text-white tracking-tight mb-8">Work</h1>
          <p className="text-2xl lg:text-3xl text-white/90 font-medium leading-relaxed max-w-5xl mb-10">
            Some of the complex challenges we’ve tackled with organisations around the world designing better experiences, better services and better futures. And we’re only just getting started.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-3 bg-primary text-white px-10 py-5 rounded-full text-sm font-bold uppercase tracking-wider hover:bg-primary/90 transition-all duration-300 whitespace-nowrap"
          >
            Get Started
            <i className="ri-arrow-right-line text-lg"></i>
          </Link>
        </div>
      </section>

      {/* Category Filter */}
      <section className="pt-16 pb-6 bg-white sticky top-24 z-40 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => handleCategoryChange(category)}
                className={`px-6 py-3 rounded-full text-sm font-bold uppercase tracking-wider whitespace-nowrap transition-all duration-200 ${
                  activeCategory === category
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies / Client List */}
      <section ref={caseStudiesSectionRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">

          {/* Client List */}
          {activeCategory === 'Clients' && (
            <div>
              <div ref={clientListRef} className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
                {clientList.map((group) => (
                  <div key={group.sector}>
                    <h3 className="text-xs font-black uppercase tracking-widest text-primary mb-4 pb-3 border-b border-gray-200">
                      {group.sector}
                    </h3>
                    <ul className="space-y-2">
                      {group.clients.map((client) => (
                        <li key={client} className="text-gray-700 text-sm font-medium leading-relaxed">
                          {client}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Case Studies Grid */}
          {activeCategory !== 'Clients' && (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredCaseStudies.map((study) => (
                  <Link
                    key={study.id}
                    to={study.link}
                    className="group cursor-pointer"
                  >
                    <div className="relative h-72 overflow-hidden mb-5 rounded-xl">
                      <img
                        src={study.image}
                        alt={study.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="space-y-2">
                      <p className="text-xs uppercase tracking-wider text-primary font-bold">{study.client}</p>
                      <h3 className="text-xl font-black text-black group-hover:text-primary transition-colors leading-snug line-clamp-3">
                        {study.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>

              {filteredCaseStudies.length === 0 && (
                <div className="text-center py-20">
                  <p className="text-2xl text-gray-400 font-medium">No case studies in this category yet.</p>
                </div>
              )}
            </>
          )}

        </div>
      </section>

      {/* Contact CTA Section */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/65e4b3a231f765d88a983851ba9becb4.jpeg"
            alt="Contact background"
            className="w-full h-full object-cover object-top"
            style={{ objectPosition: '50% 20%' }}
          />
          <div className="absolute inset-0 bg-black/40"></div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-6xl lg:text-7xl font-black text-white tracking-tight mb-8">Contact&nbsp;</h2>
          <p className="text-xl text-white/90 mb-12 font-medium">Let's discuss how we can design a better future together.</p>
          <Link to="/contact" className="bg-white text-primary px-12 py-6 rounded-full hover:bg-white/90 transition-all duration-300 text-lg font-bold uppercase tracking-wider inline-flex items-center gap-3 whitespace-nowrap">
            Get In Touch
            <i className="ri-arrow-right-line text-xl"></i>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-3 gap-12 mb-16">
            <div className="space-y-6">
              <img 
                src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/177dc540d7c9a6700435609d623348f7.png" 
                alt="Design For" 
                className="h-16"
              />
              <p className="text-white/60 text-sm leading-relaxed">Strategic consulting for ambitious businesses.</p>
            </div>

            <div>
              <h4 className="text-sm uppercase tracking-wider text-white font-bold mb-6">Navigate</h4>
              <div className="space-y-3">
                <Link to="/" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Home</Link>
                <Link to="/about" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">About</Link>
                <Link to="/services" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Services</Link>
                <Link to="/work" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Work</Link>
                <Link to="/resources" className="block text-white/70 hover:text-[#ff4858] transition-colors text-sm font-medium">Insights</Link>
                <Link to="/contact" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Contact</Link>
              </div>
            </div>

            <div>
              <h4 className="text-sm uppercase tracking-wider text-white font-bold mb-6">Follow</h4>
              <div className="flex gap-4">
                <a href="https://www.linkedin.com/in/kara-prosser-26960977/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-white/10 hover:bg-primary rounded-full flex items-center justify-center transition-colors">
                  <i className="ri-linkedin-fill text-white text-xl"></i>
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-white/40 font-medium">© 2026 Design For Consulting</p>
            <div className="flex gap-6">
              <Link to="/privacy" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Privacy</Link>
              <Link to="/terms" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Terms</Link>
              <Link to="/cookies" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Cookies</Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Back to Top Button */}
      <button
        onClick={scrollToTop}
        className={`fixed bottom-8 right-8 z-50 w-12 h-12 bg-primary text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:bg-primary/90 cursor-pointer ${
          showBackToTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
        aria-label="Back to top"
      >
        <i className="ri-arrow-up-line text-xl"></i>
      </button>
    </div>
  );
}
